import { parseMessageImageDrag, MESSAGE_IMAGE_DRAG_MIME } from "@ruta/shared";
import type { MessageImageDragPayload } from "@ruta/shared";

/**
 * Fetch a remote image URL into a File, so an image dragged out of a message
 * thread can be uploaded exactly like a manually-picked attachment. The
 * descriptor's contentType/fileName seed the result, but the fetched blob's own
 * type wins when present (more accurate than the thread's stored metadata).
 */
export async function fetchImageAsFile(
  payload: MessageImageDragPayload,
): Promise<File> {
  const response = await fetch(payload.url);
  if (!response.ok) {
    throw new Error(`Could not load image (${response.status})`);
  }
  const blob = await response.blob();
  const type = blob.type || payload.contentType;
  return new File([blob], payload.fileName, { type });
}

/**
 * Pull a message-thread image descriptor out of a drag or paste event's
 * DataTransfer, if one is present. Returns null when the transfer carries no
 * thread-image payload (e.g. an OS file drop, which the caller handles via
 * `dataTransfer.files` instead).
 */
export function readMessageImageDrag(
  dataTransfer: DataTransfer | null | undefined,
): MessageImageDragPayload | null {
  if (!dataTransfer) return null;
  return parseMessageImageDrag(dataTransfer.getData(MESSAGE_IMAGE_DRAG_MIME));
}
